/**
 * Copyright 2025 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Container
} from '@mui/material';
import { Error as ErrorIcon, Refresh } from '@mui/icons-material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container maxWidth="md">
          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4
            }}
          >
            <Card sx={{ width: '100%', maxWidth: 600 }}>
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <ErrorIcon 
                  sx={{ 
                    fontSize: 80, 
                    color: 'error.main', 
                    mb: 2 
                  }} 
                />
                
                <Typography variant="h4" gutterBottom>
                  Oops! Something went wrong
                </Typography>
                
                <Typography variant="body1" color="textSecondary" paragraph>
                  We're sorry, but something unexpected happened. Our team has been notified.
                </Typography>

                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <Box sx={{ mt: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Error Details (Development Mode)
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: 'grey.100',
                        p: 2,
                        borderRadius: 1,
                        textAlign: 'left',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        overflow: 'auto',
                        maxHeight: 200
                      }}
                    >
                      <Typography variant="body2" component="pre">
                        {this.state.error.toString()}
                        {this.state.errorInfo?.componentStack}
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<Refresh />}
                    onClick={this.handleReload}
                  >
                    Reload Page
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={this.handleReset}
                  >
                    Try Again
                  </Button>
                </Box>

                <Typography variant="caption" color="textSecondary" sx={{ mt: 3, display: 'block' }}>
                  If this problem persists, please contact support.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;